import { RadioGroup, Select, Text, TextInput } from "@gravity-ui/uikit";
import { useAtom } from "@reatom/npm-react";
import {
  AvitoFormatOptionsDto,
  AvitoFormatOptionsDtoAdTypeEnum,
  AvitoFormatOptionsDtoDescriptionOverflowPolicyEnum,
  AvitoFormatOptionsDtoTargetAudienceEnum,
  AvitoFormatOptionsDtoTitleOverflowPolicyEnum,
  ExportFormats,
} from "definitions/exporter";
import { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslate } from "src/i18n";
import { getExportOptionsRequest } from "src/pages/ExportPage/store/fetchers";

import { ExportTaskFormValues } from "../../ExportTasksForm.types";
import css from "./AvitoOptionsForm.module.scss";

const enumToOptions = (record: Record<string, string>) =>
  Object.values(record).map((value) => ({ label: value, value }));

const adTypeOptions = enumToOptions(AvitoFormatOptionsDtoAdTypeEnum);
const targetAudienceOptions = enumToOptions(
  AvitoFormatOptionsDtoTargetAudienceEnum,
);

// Бэк генерирует два разных enum-а для title и description, но семантически
// это одна и та же тройка значений. Объединяем в union, чтобы один UI-хелпер
// мог работать с обоими полями без скрытой завязки на одно из имён enum.
type OverflowPolicy =
  | AvitoFormatOptionsDtoTitleOverflowPolicyEnum
  | AvitoFormatOptionsDtoDescriptionOverflowPolicyEnum;

type OverflowPolicyOption = {
  value: OverflowPolicy;
  label: string;
  hint: string;
};

const DEFAULT_OVERFLOW_POLICY: OverflowPolicy =
  AvitoFormatOptionsDtoTitleOverflowPolicyEnum.Skip;

const isOverflowPolicy = (value: string): value is OverflowPolicy =>
  value === AvitoFormatOptionsDtoTitleOverflowPolicyEnum.Truncate ||
  value === AvitoFormatOptionsDtoTitleOverflowPolicyEnum.Skip ||
  value === AvitoFormatOptionsDtoTitleOverflowPolicyEnum.Fail;

export const AvitoOptionsForm = () => {
  const { t } = useTranslate();
  const { control, watch, setValue } = useFormContext<ExportTaskFormValues>();

  const [exportOptions] = useAtom(getExportOptionsRequest.dataAtom);

  const avitoTemplates = useMemo(() => {
    const formatInfo = exportOptions.formatsInfo?.find(
      (f) => f.format === ExportFormats.Avito,
    );
    return formatInfo?.avitoTemplates ?? [];
  }, [exportOptions.formatsInfo]);

  // Уникальные корни каталога Avito (тег <Category>), разрешённые шаблонами.
  // Avito жёстко валидирует значение — поэтому только из этого списка.
  const categoryOptions = useMemo(
    () =>
      Array.from(new Set(avitoTemplates.map((tpl) => tpl.category))).filter(
        Boolean,
      ),
    [avitoTemplates],
  );

  // Шаблон и состояние подставляет бэк автоматически по категории товара, но
  // root <Category> у всех шаблонов один — автозаполняем, если поле пустое и
  // вариант единственный, чтобы оператор не искал неочевидный обязательный селект.
  const currentCategory = watch("avitoOptions.category");
  useEffect(() => {
    if (currentCategory || categoryOptions.length !== 1) return;
    setValue("avitoOptions.category", categoryOptions[0], {
      shouldDirty: true,
    });
  }, [currentCategory, categoryOptions, setValue]);

  const titlePolicyOptions = useMemo<OverflowPolicyOption[]>(
    () => [
      {
        value: AvitoFormatOptionsDtoTitleOverflowPolicyEnum.Truncate,
        label: t(
          "export.avito.overflow.title.truncate.label",
          "Обрезать (рекомендуется)",
        ),
        hint: t(
          "export.avito.overflow.title.truncate.hint",
          "Товар попадёт в фид с обрезанным значением (по последнему слову).",
        ),
      },
      {
        value: AvitoFormatOptionsDtoTitleOverflowPolicyEnum.Skip,
        label: t("export.avito.overflow.title.skip.label", "Пропускать товар"),
        hint: t(
          "export.avito.overflow.title.skip.hint",
          "Текущее поведение. Товар не попадёт в фид, появится в списке отказов.",
        ),
      },
      {
        value: AvitoFormatOptionsDtoTitleOverflowPolicyEnum.Fail,
        label: t(
          "export.avito.overflow.title.fail.label",
          "Остановить экспорт",
        ),
        hint: t(
          "export.avito.overflow.title.fail.hint",
          "Весь экспорт упадёт с ошибкой на первом длинном поле. Для отладки.",
        ),
      },
    ],
    [t],
  );

  const descriptionPolicyOptions = useMemo<OverflowPolicyOption[]>(
    () => [
      {
        value: AvitoFormatOptionsDtoDescriptionOverflowPolicyEnum.Truncate,
        label: t(
          "export.avito.overflow.description.truncate.label",
          "Обрезать",
        ),
        hint: t(
          "export.avito.overflow.description.truncate.hint",
          "Товар попадёт в фид с обрезанным описанием (по последнему слову).",
        ),
      },
      {
        value: AvitoFormatOptionsDtoDescriptionOverflowPolicyEnum.Skip,
        label: t(
          "export.avito.overflow.description.skip.label",
          "Пропускать товар",
        ),
        hint: t(
          "export.avito.overflow.description.skip.hint",
          "Текущее поведение. Товар не попадёт в фид, появится в списке отказов.",
        ),
      },
      {
        value: AvitoFormatOptionsDtoDescriptionOverflowPolicyEnum.Fail,
        label: t(
          "export.avito.overflow.description.fail.label",
          "Остановить экспорт",
        ),
        hint: t(
          "export.avito.overflow.description.fail.hint",
          "Весь экспорт упадёт с ошибкой на первом длинном описании. Для отладки.",
        ),
      },
    ],
    [t],
  );

  const optionalSuffix = t("export.avito.optional", "(необязательно)");

  const renderPolicyField = (
    name: "titleOverflowPolicy" | "descriptionOverflowPolicy",
    label: string,
    options: OverflowPolicyOption[],
  ) => (
    <div className={css.field}>
      <Text variant="body-1" className={css.fieldLabel}>
        {label}
      </Text>
      <Controller
        name={`avitoOptions.${name}`}
        control={control}
        render={({ field }) => {
          const value = field.value ?? DEFAULT_OVERFLOW_POLICY;
          const activeHint = options.find((o) => o.value === value)?.hint;
          // Бэк-дефолт = skip. Чтобы UI-выбор skip не делал форму dirty
          // относительно persisted-таски (где поле отсутствует) и не
          // раздувал payload — пишем в RHF undefined вместо явного skip.
          const handleUpdate = (next: string) => {
            if (!isOverflowPolicy(next)) return;
            field.onChange(next === DEFAULT_OVERFLOW_POLICY ? undefined : next);
          };
          return (
            <>
              <RadioGroup
                aria-label={label}
                name={field.name}
                value={value}
                onUpdate={handleUpdate}
                direction="vertical"
                size="m"
              >
                {options.map((o) => (
                  <RadioGroup.Option key={o.value} value={o.value}>
                    {o.label}
                  </RadioGroup.Option>
                ))}
              </RadioGroup>
              <Text
                variant="caption-2"
                className={css.policyHint}
                aria-live="polite"
              >
                {activeHint ?? ""}
              </Text>
            </>
          );
        }}
      />
    </div>
  );

  const renderEnumField = (
    name: keyof AvitoFormatOptionsDto,
    label: string,
    options: { label: string; value: string }[],
    required = true,
  ) => (
    <div className={css.field}>
      <Text variant="body-1" className={css.fieldLabel}>
        {label}
        {!required && (
          <Text variant="body-1" color="secondary">
            {" "}
            {optionalSuffix}
          </Text>
        )}
      </Text>
      <Controller
        name={`avitoOptions.${name}`}
        control={control}
        rules={
          required
            ? {
                required: t(
                  "export.avito.field.required",
                  "Укажите «{{label}}»",
                  { label },
                ),
              }
            : undefined
        }
        render={({ field, fieldState }) => (
          <Select
            placeholder={t(
              "export.avito.field.placeholder",
              "Выберите «{{label}}»",
              { label: label.toLowerCase() },
            )}
            value={field.value ? [String(field.value)] : []}
            onUpdate={(value) => field.onChange(value[0])}
            validationState={fieldState.error ? "invalid" : undefined}
            errorMessage={fieldState.error?.message}
            width="max"
            size="l"
          >
            {options.map((o) => (
              <Select.Option key={o.value} value={o.value}>
                {o.label}
              </Select.Option>
            ))}
          </Select>
        )}
      />
    </div>
  );

  return (
    <div className={css.wrapper}>
      <div className={css.autoNote}>
        <Text variant="body-2" className={css.autoNoteText}>
          {t(
            "export.avito.autoNote",
            "Шаблон Avito и состояние «Новое с биркой» подставляются автоматически для каждой категории товара. Товары неподдерживаемых категорий в фид не попадут.",
          )}
        </Text>
      </div>

      <div className={css.field}>
        <Text variant="body-1" className={css.fieldLabel}>
          {t("export.avito.category.label", "Категория Avito")}
        </Text>
        <Controller
          name="avitoOptions.category"
          control={control}
          rules={{
            required: t("export.avito.category.required", "Выберите категорию"),
          }}
          render={({ field, fieldState }) => (
            <Select
              placeholder={t(
                "export.avito.category.placeholder",
                "Выберите категорию",
              )}
              value={field.value ? [field.value] : []}
              onUpdate={(value) => field.onChange(value[0])}
              validationState={fieldState.error ? "invalid" : undefined}
              errorMessage={fieldState.error?.message}
              width="max"
              size="l"
            >
              {categoryOptions.map((v) => (
                <Select.Option key={v} value={v}>
                  {v}
                </Select.Option>
              ))}
            </Select>
          )}
        />
      </div>

      {renderEnumField(
        "adType",
        t("export.avito.adType", "Тип объявления"),
        adTypeOptions,
      )}
      {renderEnumField(
        "targetAudience",
        t("export.avito.targetAudience", "Целевая аудитория"),
        targetAudienceOptions,
        false,
      )}

      <div className={css.field}>
        <Text variant="body-1" className={css.fieldLabel}>
          {t("export.avito.address.label", "Адрес")}
          <Text variant="body-1" color="secondary">
            {" "}
            {optionalSuffix}
          </Text>
        </Text>
        <Controller
          name="avitoOptions.address"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              value={field.value || ""}
              placeholder={t(
                "export.avito.address.placeholder",
                "Передаётся в тег <Address>",
              )}
              size="l"
            />
          )}
        />
      </div>

      <div className={css.overflowGroup}>
        <div className={css.overflowGroupHeader}>
          <Text variant="subheader-1" className={css.overflowGroupTitle}>
            {t(
              "export.avito.overflow.title",
              "Что делать с длинными значениями",
            )}
          </Text>
          <Text variant="body-1" className={css.overflowGroupSubtitle}>
            {t(
              "export.avito.overflow.subtitle",
              "У Avito жёсткие лимиты на Title (50) и Description (7500). Если значение превышает лимит — товар будет обработан по выбранной политике.",
            )}
          </Text>
        </div>

        {renderPolicyField(
          "titleOverflowPolicy",
          t(
            "export.avito.overflow.titleField",
            "Длинные названия (Title > 50 символов)",
          ),
          titlePolicyOptions,
        )}

        {renderPolicyField(
          "descriptionOverflowPolicy",
          t(
            "export.avito.overflow.descriptionField",
            "Длинные описания (Description > 7500 символов)",
          ),
          descriptionPolicyOptions,
        )}
      </div>
    </div>
  );
};
